import { useEffect } from 'react';

interface NudgeModalProps {
  /** モーダルが表示されているかどうか */
  isOpen: boolean;
  /** モーダルを閉じる関数 */
  onClose: () => void;
  /** モーダルのタイトル */
  title: string;
  /** モーダルの補足説明 */
  description: string;
  /** 主ボタンのラベル */
  primaryButtonLabel: string;
  /** 主ボタンのクリックハンドラー */
  onPrimaryClick: () => void;
  /** 副ボタンのラベル（デフォルト: "キャンセル"） */
  secondaryButtonLabel?: string;
}

/**
 * ナッジモーダルコンポーネント
 * 
 * 口コミやレシピの投稿を促すための小さめのモーダル
 */
export default function NudgeModal({
  isOpen,
  onClose,
  title,
  description,
  primaryButtonLabel,
  onPrimaryClick,
  secondaryButtonLabel = 'キャンセル',
}: NudgeModalProps) {
  // ESCキーで閉じる
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // モーダルが開いているときはbodyのスクロールを防ぐ
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* 背景オーバーレイ */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* モーダルコンテンツ */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6 animate-fadeIn">
        {/* 閉じるボタン */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer"
          aria-label="閉じる"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        {/* タイトル */}
        <h3 className="text-xl font-bold text-gray-900 mb-3 pr-8">
          {title}
        </h3>

        {/* 説明文 */}
        <p className="text-gray-600 mb-6 text-sm leading-relaxed">
          {description}
        </p>

        {/* ボタン */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            {secondaryButtonLabel}
          </button>
          <button
            type="button"
            onClick={() => {
              onPrimaryClick();
              onClose();
            }}
            className="flex-1 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium cursor-pointer whitespace-nowrap"
          >
            {primaryButtonLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
